import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  FileText, 
  ChevronDown,
  ChevronUp,
  Filter,
  Plus,
  Trash2,
  Send,
  Save,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Pencil,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OperationsHub = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = user?.role === 'super_admin';

  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const [waybills, setWaybills] = useState([]);
  const [approvalWaybills, setApprovalWaybills] = useState([]);
  const [acceptanceWaybills, setAcceptanceWaybills] = useState([]);
  const [newWaybills, setNewWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [approvalPage, setApprovalPage] = useState(1);
  const [acceptancePage, setAcceptancePage] = useState(1);
  const [newWaybillsPage, setNewWaybillsPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [approvalTotal, setApprovalTotal] = useState(0);
  const [acceptanceTotal, setAcceptanceTotal] = useState(0);
  const [newWaybillsTotal, setNewWaybillsTotal] = useState(0);
  const [expandedWaybill, setExpandedWaybill] = useState(null);
  const [itemCosts, setItemCosts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const limit = 10;

  const initCosts = (waybillList) => {
    const costs = {};
    waybillList.forEach(wb => {
      costs[wb.waybill_id] = {};
      wb.items?.forEach(item => {
        costs[wb.waybill_id][item.item_id] = {
          third_party_cost: item.third_party_cost || 0,
          additional_cost: item.additional_cost || 0,
          markup_percentage: item.markup_percentage || 15,
        };
      });
    });
    return costs;
  };

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/dashboard/operations-stats`),
        axios.get(`${API_URL}/api/waybills/pending-quotes?page=${page}&limit=${limit}`),
        axios.get(`${API_URL}/api/waybills/awaiting-acceptance?page=${acceptancePage}&limit=${limit}`),
        axios.get(`${API_URL}/api/waybills/new-waybills?page=${newWaybillsPage}&limit=${limit}`)
      ];
      if (isSuperAdmin) {
        requests.push(axios.get(`${API_URL}/api/waybills/pending-approval?page=${approvalPage}&limit=${limit}`));
      }

      const results = await Promise.all(requests);

      setStats(results[0].data);
      setWaybills(results[1].data.waybills);
      setTotal(results[1].data.total);
      setAcceptanceWaybills(results[2].data.waybills);
      setAcceptanceTotal(results[2].data.total);
      setNewWaybills(results[3].data.waybills);
      setNewWaybillsTotal(results[3].data.total);

      const pendingCosts = initCosts(results[1].data.waybills);

      if (isSuperAdmin && results[4]) {
        setApprovalWaybills(results[4].data.waybills);
        setApprovalTotal(results[4].data.total);
        const approvalCosts = initCosts(results[4].data.waybills);
        setItemCosts({ ...pendingCosts, ...approvalCosts });
      } else {
        setItemCosts(pendingCosts);
      }

      // Auto-expand from URL param
      const expandId = searchParams.get('expand');
      if (expandId) {
        const allWb = [...results[1].data.waybills, ...(results[4]?.data?.waybills || [])];
        const found = allWb.find(wb => wb.waybill_id === expandId);
        if (found) {
          setExpandedWaybill(expandId);
          if (found.status === 'pending_approval' && isSuperAdmin) {
            setActiveTab('approval');
          }
        }
        setSearchParams({}, { replace: true });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [page, approvalPage, acceptancePage, newWaybillsPage, isSuperAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (waybillId) => {
    setExpandedWaybill(expandedWaybill === waybillId ? null : waybillId);
  };

  const updateItemCost = (waybillId, itemId, field, value) => {
    setItemCosts(prev => ({
      ...prev,
      [waybillId]: {
        ...prev[waybillId],
        [itemId]: {
          ...prev[waybillId]?.[itemId],
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  const calculateLineTotal = (waybillId, itemId) => {
    const costs = itemCosts[waybillId]?.[itemId] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
    const baseCost = costs.third_party_cost + costs.additional_cost;
    return baseCost + baseCost * (costs.markup_percentage / 100);
  };

  const calculateTotals = (waybillId, waybillList) => {
    const waybill = waybillList.find(wb => wb.waybill_id === waybillId);
    if (!waybill) return { subtotal: 0, markup: 0, total: 0 };
    let subtotal = 0, markup = 0;
    waybill.items?.forEach(item => {
      const costs = itemCosts[waybillId]?.[item.item_id] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
      const baseCost = costs.third_party_cost + costs.additional_cost;
      subtotal += baseCost;
      markup += baseCost * (costs.markup_percentage / 100);
    });
    return { subtotal, markup, total: subtotal + markup };
  };

  const submitQuote = async (waybillId, isDraft = false, forApproval = false) => {
    setSubmitting(true);
    try {
      const allWb = [...waybills, ...approvalWaybills];
      const waybill = allWb.find(wb => wb.waybill_id === waybillId);
      const items = waybill.items?.map(item => ({
        item_id: item.item_id,
        third_party_cost: itemCosts[waybillId]?.[item.item_id]?.third_party_cost || 0,
        additional_cost: itemCosts[waybillId]?.[item.item_id]?.additional_cost || 0,
        markup_percentage: itemCosts[waybillId]?.[item.item_id]?.markup_percentage || 0,
      })) || [];

      await axios.put(`${API_URL}/api/waybills/${waybillId}/line-item-quote`, {
        items,
        is_draft: isDraft,
        request_approval: forApproval
      });

      if (isDraft) {
        toast.success('Draft saved');
      } else if (forApproval) {
        toast.success('Quote sent for approval');
      } else {
        toast.success('Quote sent to client');
      }

      if (!isDraft) {
        fetchData();
        setExpandedWaybill(null);
      }
    } catch (err) {
      toast.error(getApiError(err, 'Failed to submit quote'));
    } finally {
      setSubmitting(false);
    }
  };

  const approveQuote = async (waybillId, withEdits = false) => {
    setSubmitting(true);
    try {
      let payload = null;
      if (withEdits) {
        const waybill = approvalWaybills.find(wb => wb.waybill_id === waybillId);
        const items = waybill.items?.map(item => ({
          item_id: item.item_id,
          third_party_cost: itemCosts[waybillId]?.[item.item_id]?.third_party_cost || 0,
          additional_cost: itemCosts[waybillId]?.[item.item_id]?.additional_cost || 0,
          markup_percentage: itemCosts[waybillId]?.[item.item_id]?.markup_percentage || 0,
        })) || [];
        payload = { items, is_draft: false };
      }

      await axios.put(`${API_URL}/api/waybills/${waybillId}/approve-quote`, payload);
      toast.success('Quote approved and sent to client');
      fetchData();
      setExpandedWaybill(null);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to approve quote'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R ${Number(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const getCityCode = (city) => {
    const codes = {
      'Johannesburg': 'JHB', 'Cape Town': 'CPT', 'Durban': 'DUR', 'Pretoria': 'PTA',
      'Port Elizabeth': 'PLZ', 'Gqeberha': 'PLZ', 'Bloemfontein': 'BFN', 'Polokwane': 'POL',
      'Durban Port': 'DUR', 'Pretoria Central': 'PTA',
    };
    return codes[city] || city?.substring(0, 3).toUpperCase();
  };

  const renderWaybillRow = (waybill, waybillList, isApproval = false) => {
    const isExpanded = expandedWaybill === waybill.waybill_id;
    const totals = calculateTotals(waybill.waybill_id, waybillList);

    return (
      <div key={waybill.waybill_id} className="bg-white" data-testid={`waybill-row-${waybill.reference_number}`}>
        {/* Collapsed Row */}
        <div 
          className="grid grid-cols-12 gap-4 px-6 py-4 items-center cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleExpand(waybill.waybill_id)}
        >
          <div className="col-span-2">
            <span className="text-teal-700 font-semibold">#{waybill.reference_number}</span>
          </div>
          <div className="col-span-3">
            <div className="flex items-center space-x-3">
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                {waybill.customer?.company_name?.substring(0, 2).toUpperCase() || 'CL'}
              </div>
              <div>
                <span className="font-medium text-slate-900">{waybill.customer?.company_name || waybill.sender_company}</span>
                {isApproval && waybill.submitted_by_name && (
                  <p className="text-xs text-purple-600">Submitted by {waybill.submitted_by_name}</p>
                )}
                {!isApproval && waybill.quoted_by_name && (
                  <p className="text-xs text-slate-500">Quoted by {waybill.quoted_by_name}</p>
                )}
              </div>
            </div>
          </div>
          <div className="col-span-4">
            <span className="text-slate-900">{waybill.origin_city}</span>
            <span className="text-slate-400 mx-2">({getCityCode(waybill.origin_city)})</span>
            <span className="text-slate-400">&rarr;</span>
            <span className="text-slate-900 ml-2">{waybill.destination_city}</span>
            <span className="text-slate-400 mx-2">({getCityCode(waybill.destination_city)})</span>
          </div>
          <div className="col-span-2">
            {isApproval ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">
                PENDING APPROVAL
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                PENDING QUOTE
              </span>
            )}
          </div>
          <div className="col-span-1 text-right">
            {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400 inline" /> : <ChevronDown className="h-5 w-5 text-slate-400 inline" />}
          </div>
        </div>

        {/* Expanded Detail */}
        {isExpanded && (
          <div className="px-6 pb-6 bg-slate-50/50" data-testid={`waybill-details-${waybill.reference_number}`}>
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-8">
                  <h4 className="text-lg font-semibold text-slate-900 mb-1">Detailed Item Lines</h4>
                  <p className="text-sm text-slate-500 mb-6">
                    {isApproval 
                      ? 'Review and optionally edit costs before approving.' 
                      : 'Update line costs and markups to generate freight total for this request.'}
                  </p>

                  <div className="space-y-6">
                    {waybill.items?.map((item, idx) => (
                      <div key={item.item_id} className="bg-white rounded-xl p-5 border border-slate-100">
                        <div className="flex items-end gap-3 mb-4">
                          <div className="flex-1 min-w-0">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Description</label>
                            <Input value={item.description || `Item ${idx + 1}`} readOnly className="bg-slate-50" />
                          </div>
                          {item.item_reference && (
                            <div className="w-28 flex-shrink-0">
                              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Ref #</label>
                              <Input value={item.item_reference} readOnly className="bg-slate-50 text-center" />
                            </div>
                          )}
                          <div className="w-16 flex-shrink-0">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Qty</label>
                            <Input value={item.quantity} readOnly className="bg-slate-50 text-center" />
                          </div>
                          <div className="w-20 flex-shrink-0">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Weight</label>
                            <Input value={`${item.weight}kg`} readOnly className="bg-slate-50 text-center" />
                          </div>
                          <div className="w-20 flex-shrink-0">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">L (CM)</label>
                            <Input value={item.length} readOnly className="bg-slate-50 text-center" />
                          </div>
                          <div className="flex-shrink-0">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">W × H (CM)</label>
                            <div className="flex items-center space-x-1">
                              <Input value={item.width} readOnly className="bg-slate-50 text-center w-14" />
                              <span className="text-slate-400">×</span>
                              <Input value={item.height} readOnly className="bg-slate-50 text-center w-14" />
                            </div>
                          </div>
                          {!isApproval && (
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 flex-shrink-0">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-4 gap-4 items-end">
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">3rd Party Cost (R)</label>
                            <Input
                              type="number"
                              value={itemCosts[waybill.waybill_id]?.[item.item_id]?.third_party_cost || ''}
                              onChange={(e) => updateItemCost(waybill.waybill_id, item.item_id, 'third_party_cost', e.target.value)}
                              placeholder="0"
                              className="bg-slate-800 text-white border-slate-700"
                              data-testid={`third-party-cost-${item.item_id}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Additional Cost (R)</label>
                            <Input
                              type="number"
                              value={itemCosts[waybill.waybill_id]?.[item.item_id]?.additional_cost || ''}
                              onChange={(e) => updateItemCost(waybill.waybill_id, item.item_id, 'additional_cost', e.target.value)}
                              placeholder="0"
                              className="bg-slate-800 text-white border-slate-700"
                              data-testid={`additional-cost-${item.item_id}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Markup (%)</label>
                            <Input
                              type="number"
                              value={itemCosts[waybill.waybill_id]?.[item.item_id]?.markup_percentage || ''}
                              onChange={(e) => updateItemCost(waybill.waybill_id, item.item_id, 'markup_percentage', e.target.value)}
                              placeholder="15"
                              className="bg-slate-800 text-white border-slate-700"
                              data-testid={`markup-${item.item_id}`}
                            />
                          </div>
                          <div className="text-right">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Line Total</label>
                            <p className="text-xl font-bold text-slate-900">
                              {formatCurrency(calculateLineTotal(waybill.waybill_id, item.item_id))}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quote Summary Sidebar */}
                <div className="w-64 flex-shrink-0">
                  <div className="bg-white rounded-xl p-5 border border-slate-100 sticky top-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-teal-600 mb-2">Base Freight Total</p>
                    <p className="text-4xl font-bold text-teal-700 mb-4">{formatCurrency(totals.total)}</p>
                    <div className="space-y-2 text-sm border-t border-slate-100 pt-4">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Subtotal Cost</span>
                        <span className="font-medium text-slate-900">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Markup</span>
                        <span className="font-medium text-slate-900">{formatCurrency(totals.markup)}</span>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      {isApproval ? (
                        <>
                          <Button 
                            onClick={() => approveQuote(waybill.waybill_id, true)}
                            disabled={submitting || totals.total === 0}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`approve-send-${waybill.reference_number}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            {submitting ? 'Approving...' : 'Approve & Send'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            onClick={() => submitQuote(waybill.waybill_id, false, false)}
                            disabled={submitting || totals.total === 0}
                            className="w-full btn-primary"
                            data-testid={`submit-quote-${waybill.reference_number}`}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {submitting ? 'Sending...' : 'Submit Quote'}
                          </Button>
                          {!isSuperAdmin && (
                            <Button 
                              onClick={() => submitQuote(waybill.waybill_id, false, true)}
                              disabled={submitting || totals.total === 0}
                              variant="outline"
                              className="w-full text-purple-700 border-purple-200 hover:bg-purple-50"
                              data-testid={`send-approval-${waybill.reference_number}`}
                            >
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              Send for Approval
                            </Button>
                          )}
                          <Button 
                            onClick={() => submitQuote(waybill.waybill_id, true)}
                            disabled={submitting}
                            variant="ghost"
                            className="w-full text-slate-600"
                            data-testid={`save-draft-${waybill.reference_number}`}
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Save Draft
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPagination = (currentPage, setCurrentPage, totalItems) => {
    const totalPages = Math.ceil(totalItems / limit);
    if (totalPages <= 0) return null;
    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
        <p className="text-sm text-slate-500">
          Showing {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, totalItems)} of {totalItems}
        </p>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 p-0">&lsaquo;</Button>
          {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(num => (
            <Button key={num} variant={currentPage === num ? 'default' : 'outline'} size="sm" onClick={() => setCurrentPage(num)} className={`w-8 h-8 p-0 ${currentPage === num ? 'bg-slate-900 text-white' : ''}`}>{num}</Button>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 p-0">&rsaquo;</Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="operations-hub">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
              <FileText className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <h1 className="page-title">Operations Hub</h1>
              <p className="text-slate-600">Manage and quote pending logistics requests.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="border-slate-200">
              <Filter className="h-4 w-4 mr-2" />
              Filter View
            </Button>
            <Link to="/admin/quote-queue">
              <Button className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Waybill
              </Button>
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className={`grid ${isSuperAdmin ? 'grid-cols-2 lg:grid-cols-7' : 'grid-cols-2 lg:grid-cols-6'} gap-4 mb-8`}>
          <Card className="card-elevated" data-testid="stat-pending-quotes">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Pending Quotes</p>
              <p className="text-3xl font-bold text-teal-700 mt-1">{stats?.pending_quotes || 0}</p>
              <div className="h-1 bg-teal-100 rounded-full mt-3">
                <div className="h-full bg-teal-500 rounded-full" style={{ width: '60%' }}></div>
              </div>
            </CardContent>
          </Card>

          {isSuperAdmin && (
            <Card className="card-elevated" data-testid="stat-pending-approval">
              <CardContent className="p-5">
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Awaiting Approval</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">{stats?.pending_approval || 0}</p>
                <div className="h-1 bg-purple-100 rounded-full mt-3">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: stats?.pending_approval > 0 ? '80%' : '0%' }}></div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card-elevated" data-testid="stat-awaiting-acceptance">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Awaiting Client</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats?.awaiting_acceptance || 0}</p>
              <div className="h-1 bg-orange-100 rounded-full mt-3">
                <div className="h-full bg-orange-500 rounded-full" style={{ width: stats?.awaiting_acceptance > 0 ? '70%' : '0%' }}></div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-new-waybills">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">New Waybills</p>
              <p className="text-3xl font-bold text-emerald-600 mt-1">{stats?.new_waybills || 0}</p>
              <div className="h-1 bg-emerald-100 rounded-full mt-3">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: stats?.new_waybills > 0 ? '60%' : '0%' }}></div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-in-transit">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">In Transit</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.in_transit || 0}</p>
              <div className="h-1 bg-slate-100 rounded-full mt-3">
                <div className="h-full bg-slate-400 rounded-full" style={{ width: '45%' }}></div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-turnaround">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Avg. Turnaround</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.avg_turnaround || 0}h</p>
              <div className="h-1 bg-slate-100 rounded-full mt-3">
                <div className="h-full bg-slate-400 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-exceptions">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Exceptions</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats?.exceptions || 0}</p>
              <div className="h-1 bg-orange-100 rounded-full mt-3">
                <div className="h-full bg-orange-500 rounded-full" style={{ width: '15%' }}></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-100 rounded-lg p-1 w-fit" data-testid="ops-hub-tabs">
          <button
            onClick={() => { setActiveTab('pending'); setExpandedWaybill(null); }}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-pending-quotes"
          >
            <Clock className="h-4 w-4 mr-2" />
            Pending Quotes
            {total > 0 && <span className="ml-2 bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">{total}</span>}
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => { setActiveTab('approval'); setExpandedWaybill(null); }}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'approval' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="tab-pending-approval"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Awaiting Approval
              {approvalTotal > 0 && <span className="ml-2 bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full">{approvalTotal}</span>}
            </button>
          )}
          <button
            onClick={() => { setActiveTab('acceptance'); setExpandedWaybill(null); }}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'acceptance' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-awaiting-acceptance"
          >
            <FileText className="h-4 w-4 mr-2" />
            Awaiting Client
            {acceptanceTotal > 0 && <span className="ml-2 bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-0.5 rounded-full">{acceptanceTotal}</span>}
          </button>
          <button
            onClick={() => { setActiveTab('new'); setExpandedWaybill(null); }}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'new' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-new-waybills"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            New Waybills
            {newWaybillsTotal > 0 && <span className="ml-2 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">{newWaybillsTotal}</span>}
          </button>
        </div>

        {/* Pending Quotes Tab */}
        {activeTab === 'pending' && (
          <Card className="card-elevated">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <CardTitle className="card-title">Pending Waybills</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {waybills.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Waybill ID</div>
                    <div className="col-span-3">Client</div>
                    <div className="col-span-4">Route</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>
                  {waybills.map(wb => (
                    <Link
                      key={wb.waybill_id}
                      to={`/admin/shipment/${wb.waybill_id}`}
                      state={{ from: '/admin/operations' }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`pending-row-${wb.reference_number}`}
                    >
                      <div className="col-span-2">
                        <span className="text-teal-700 font-semibold">#{wb.reference_number}</span>
                      </div>
                      <div className="col-span-3">
                        <div className="flex items-center space-x-3">
                          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                            {wb.customer?.company_name?.substring(0, 2).toUpperCase() || 'CL'}
                          </div>
                          <span className="font-medium text-slate-900">{wb.customer?.company_name || wb.sender_company}</span>
                        </div>
                      </div>
                      <div className="col-span-4 text-sm text-slate-700">
                        {wb.origin_city} <span className="text-slate-400 mx-1">→</span> {wb.destination_city}
                      </div>
                      <div className="col-span-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                          PENDING QUOTE
                        </span>
                      </div>
                      <div className="col-span-1 text-right">
                        <ArrowRight className="h-5 w-5 text-slate-400 inline" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">No pending waybills to quote</div>
              )}
              {renderPagination(page, setPage, total)}
            </CardContent>
          </Card>
        )}

        {/* Approval Tab (Super Admin only) */}
        {activeTab === 'approval' && isSuperAdmin && (
          <Card className="card-elevated">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="card-title">Awaiting Your Approval</CardTitle>
                    <p className="text-sm text-slate-500 mt-0.5">Quotes submitted by your team, ready for review.</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {approvalWaybills.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Waybill ID</div>
                    <div className="col-span-3">Client</div>
                    <div className="col-span-3">Route</div>
                    <div className="col-span-2">Submitted By</div>
                    <div className="col-span-2 text-right">Action</div>
                  </div>
                  {approvalWaybills.map(wb => (
                    <Link
                      key={wb.waybill_id}
                      to={`/admin/shipment/${wb.waybill_id}`}
                      state={{ from: '/admin/operations' }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`approval-row-${wb.reference_number}`}
                    >
                      <div className="col-span-2">
                        <span className="text-teal-700 font-semibold">#{wb.reference_number}</span>
                      </div>
                      <div className="col-span-3">
                        <span className="font-medium text-slate-900">{wb.customer?.company_name || wb.sender_company}</span>
                      </div>
                      <div className="col-span-3 text-sm text-slate-700">
                        {wb.origin_city} <span className="text-slate-400 mx-1">→</span> {wb.destination_city}
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-purple-600">{wb.submitted_by_name || '-'}</span>
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">
                          PENDING APPROVAL
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <p>No quotes pending approval</p>
                </div>
              )}
              {renderPagination(approvalPage, setApprovalPage, approvalTotal)}
            </CardContent>
          </Card>
        )}

        {/* Awaiting Client Acceptance Tab */}
        {activeTab === 'acceptance' && (
          <Card className="card-elevated">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="card-title">Awaiting Client Acceptance</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">Quotes sent to clients, pending their response.</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {acceptanceWaybills.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Waybill ID</div>
                    <div className="col-span-3">Client</div>
                    <div className="col-span-3">Route</div>
                    <div className="col-span-2 text-right">Quoted Amount</div>
                    <div className="col-span-2 text-right">Quoted Date</div>
                  </div>
                  {acceptanceWaybills.map(wb => (
                    <Link
                      key={wb.waybill_id}
                      to={`/admin/shipment/${wb.waybill_id}`}
                      state={{ from: '/admin/operations' }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`acceptance-row-${wb.reference_number}`}
                    >
                      <div className="col-span-2">
                        <span className="text-teal-700 font-semibold">{wb.reference_number}</span>
                      </div>
                      <div className="col-span-3">
                        <p className="font-medium text-slate-900">{wb.customer?.company_name || wb.customer?.full_name || '-'}</p>
                        <p className="text-xs text-slate-500">{wb.customer?.email || ''}</p>
                      </div>
                      <div className="col-span-3 text-sm text-slate-700">
                        {wb.origin_city} <span className="text-slate-400 mx-1">→</span> {wb.destination_city}
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="font-semibold text-slate-900">{formatCurrency(wb.quoted_amount)}</span>
                      </div>
                      <div className="col-span-2 text-right text-sm text-slate-500">
                        {wb.quoted_at ? new Date(wb.quoted_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <p>No quotes awaiting client acceptance</p>
                </div>
              )}
              {renderPagination(acceptancePage, setAcceptancePage, acceptanceTotal)}
            </CardContent>
          </Card>
        )}

        {/* New Waybills Tab */}
        {activeTab === 'new' && (
          <Card className="card-elevated">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="card-title">New Waybills</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">Accepted quotes and direct ship-now orders ready for dispatch.</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {newWaybills.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Waybill ID</div>
                    <div className="col-span-2">Client</div>
                    <div className="col-span-2">Route</div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-2 text-right">Amount</div>
                    <div className="col-span-2 text-right">Accepted</div>
                  </div>
                  {newWaybills.map(wb => (
                    <Link
                      key={wb.waybill_id}
                      to={`/admin/shipment/${wb.waybill_id}`}
                      state={{ from: '/admin/operations' }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`new-waybill-row-${wb.reference_number}`}
                    >
                      <div className="col-span-2">
                        <span className="text-teal-700 font-semibold">{wb.reference_number}</span>
                      </div>
                      <div className="col-span-2">
                        <p className="font-medium text-slate-900 text-sm">{wb.customer?.company_name || '-'}</p>
                        <p className="text-xs text-slate-500">{wb.customer?.email || ''}</p>
                      </div>
                      <div className="col-span-2 text-sm text-slate-700">
                        {wb.origin_city} <span className="text-slate-400 mx-1">&rarr;</span> {wb.destination_city}
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          wb.submit_type === 'direct' 
                            ? 'bg-blue-50 text-blue-700 border border-blue-100' 
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        }`}>
                          {wb.submit_type === 'direct' ? 'Ship Now' : 'Quote Accepted'}
                        </span>
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="font-semibold text-slate-900">{wb.quoted_amount ? `R ${Number(wb.quoted_amount).toLocaleString('en-ZA', {minimumFractionDigits: 2})}` : '-'}</span>
                      </div>
                      <div className="col-span-2 text-right text-sm text-slate-500">
                        {wb.accepted_date ? new Date(wb.accepted_date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p>No new waybills to action</p>
                </div>
              )}
              {renderPagination(newWaybillsPage, setNewWaybillsPage, newWaybillsTotal)}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default OperationsHub;
